import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit'
import Pubnub, { ChannelMembershipObject, ObjectCustom } from 'pubnub'
import { REHYDRATE } from 'redux-persist'

import type { RootState } from 'src/redux/store'
import { Classified } from 'src/rest/types/Classified'

const name = 'chat'
export type UnreadMessagesCount = { [key: string]: number }

type ChatState = {
  memberships: ChannelMembershipObject<ObjectCustom, ObjectCustom>[] | null
  currentChannel: string | null
  usersMetadata: Pubnub.UUIDMetadataObject<Pubnub.ObjectCustom>[] | null
  unreadMessagesCount: UnreadMessagesCount
}

//
// Initial state
//

const initialState: ChatState = {
  memberships: null,
  currentChannel: null,
  usersMetadata: null,
  unreadMessagesCount: {},
}

//
// Slice (Actions & Reducers)
//

const slice = createSlice({
  name,
  initialState,
  reducers: {
    init: () => undefined,
    setMemberships: (
      state,
      action: PayloadAction<
        ChannelMembershipObject<ObjectCustom, ObjectCustom>[] | null
      >
    ) => {
      state.memberships = action.payload
    },
    setCurrentChannel: (state, action: PayloadAction<string | null>) => {
      state.currentChannel = action.payload
    },
    setUnreadMessagesCount: (
      state,
      action: PayloadAction<UnreadMessagesCount>
    ) => {
      state.unreadMessagesCount = action.payload
    },
    setUsersMetadata: (
      state,
      action: PayloadAction<Pubnub.UUIDMetadataObject<Pubnub.ObjectCustom>[]>
    ) => {
      state.usersMetadata = action.payload
    },
    createNewChannel: (
      _state,

      _action: PayloadAction<{
        message: string
        classifiedAuthor: Classified
        response: string | number
      }>
    ) => undefined,
    sendFirstMessage: (
      _state,

      _action: PayloadAction<{
        message: string
        classifiedAuthor: Classified | undefined
      }>
    ) => undefined,
    refreshUnreadCount: () => undefined,
  },
  extraReducers: (builder) =>
    builder.addCase<any, PayloadAction<any>>(REHYDRATE, () => {
      return { ...initialState }
    }),
})

export const { reducer, actions } = slice

//
// Selectors
//

const root = (state: RootState) => state[name]
const memberships = (state: RootState) => root(state).memberships
const currentChannel = (state: RootState) => root(state).currentChannel
const usersMetadata = (state: RootState) => root(state).usersMetadata
const unreadMessagesCount = (state: RootState) =>
  root(state).unreadMessagesCount
const hasUnreadMessages = createSelector(
  [unreadMessagesCount],
  (count) =>
    Object.entries(count)
      // Ignore leftover `channel_..._NaN_..._` channels from the pre-fix window:
      // those conversations are merged/unrecoverable and can't be surfaced.
      .filter(([channelId]) => !channelId.includes('NaN'))
      .reduce(
        (accumulator, [, currentValue]) => accumulator + currentValue,
        0
      ) > 0
)
const unreadMessagesCountForChannels = createSelector(
  [
    unreadMessagesCount,
    (_state: RootState, channelIds: string[]) => channelIds,
  ],
  (channelsMap, channelIds) =>
    channelIds.reduce((filteredChannelsMap, channelId) => {
      if (channelsMap[channelId] !== undefined) {
        filteredChannelsMap[channelId] = channelsMap[channelId]
      }
      return filteredChannelsMap
    }, {} as UnreadMessagesCount)
)
const hasUnreadMessagesForChannels = createSelector(
  [unreadMessagesCountForChannels],
  (channelsMap) =>
    Object.values(channelsMap).reduce(
      (unreadCount, currentValue) => unreadCount + currentValue,
      0
    ) > 0
)

export const selectors = {
  memberships,
  currentChannel,
  usersMetadata,
  unreadMessagesCount,
  hasUnreadMessages,
  unreadMessagesCountForChannels,
  hasUnreadMessagesForChannels,
}
